import React, { createContext, useState, useContext, useEffect } from 'react';
import translations from '../locales/translations.json';
import logger from '../utils/logger';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Default to English
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const savedLanguage = localStorage.getItem('bedceo_language');
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'es')) {
      return savedLanguage;
    }

    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'es') {
      return 'es';
    }

    // Default to English
    return 'en';
  });

  useEffect(() => {
    // Save to localStorage when language changes
    localStorage.setItem('bedceo_language', language);

    // Update HTML lang attribute for SEO
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        logger.warn(`Translation key not found: ${key} for language: ${language}`);
        return key;
      }
    }

    return value || key;
  };

  const changeLanguage = (lang) => {
    if (lang === 'en' || lang === 'es') {
      setLanguage(lang);
    } else {
      logger.warn(`Language ${lang} is not supported. Using English as default.`);
      setLanguage('en');
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
